/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { toJS } from 'mobx';
import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { IHeader, IOrderHistoryViewProps } from './order-history';
import OrderHistory from './components/order-history-table';
import Alert from '../../components/alert';

export const OrderHistoryOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    channelName,
    createdDate,
    count,
    amount,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {channelReferenceId}
    </Node>
);

export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

export const OrderHistoryGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderHistoryGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderHistoryGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, heading, orderHistoryOrderCountLabel, extraActions }) => (
    <Node {...headerProps}>
        {heading}
        {orderHistoryOrderCountLabel}
        {extraActions}
    </Node>
);

const OrderHistoryView: React.FC<IOrderHistoryViewProps> = ({
    orderHistoryProps,
    header,
    pageSize,
    resources,
    context,
    orderHistoryTableData,
    soldTosFetched,
    soldToIdsData
}) => {
    const [globalFilter, setGlobalFilter] = React.useState('');

    return (
        <Module {...orderHistoryProps}>
            {context.actionContext.requestContext.user.isAuthenticated ? (
                <>
                    {header && <OrderHistoryHeader {...header} />}
                    {soldTosFetched && (
                        <OrderHistory
                            pageSize={pageSize}
                            resources={resources}
                            context={context}
                            globalFilter={globalFilter}
                            setGlobalFilter={setGlobalFilter}
                            orderHistoryTableData={toJS(orderHistoryTableData)}
                            soldToIdsData={soldToIdsData}
                        ></OrderHistory>
                    )}
                </>
            ) : (
                <>
                    <div className='msc-alert__container'>
                        <Alert message={resources.orderHistoryNotLoggedIn} type='info' iconSize={1} />
                        {
                            <p
                                className='msc-alert__explanation'
                                dangerouslySetInnerHTML={{ __html: resources.orderHistoryNotLoggedInExplanation }}
                            />
                        }
                        <a className='msc-cta__primary' href={context.request.user.signInUrl}>
                            {resources.orderHistoryLogIn}
                        </a>
                    </div>
                </>
            )}
        </Module>
    );
};

export default OrderHistoryView;
